import React, { useEffect, useState } from 'react';
import { HiCheck, HiChevronLeft, HiChevronRight, HiX } from 'react-icons/hi';
import ModalOverlay from './ModalOverlay';

/** Локальная дата YYYY-MM-DD (без UTC) */
export const formatDateInput = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const parseLocalYmd = (value, fallback = new Date()) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return fallback instanceof Date ? new Date(fallback) : new Date();
  }
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const defaultRange = (daysBack = 30) => {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - daysBack);
  return { from: formatDateInput(from), to: formatDateInput(to) };
};

export const formatDateRange = (from, to) => {
  if (!from || !to) return '';
  const fromDate = parseLocalYmd(from);
  const toDate = parseLocalYmd(to);
  const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  if (fromDate.getMonth() === toDate.getMonth() && fromDate.getFullYear() === toDate.getFullYear()) {
    return `${fromDate.getDate()} - ${toDate.getDate()} ${months[toDate.getMonth()]}, ${toDate.getFullYear()}`;
  }
  return `${fromDate.getDate()} ${months[fromDate.getMonth()]} - ${toDate.getDate()} ${months[toDate.getMonth()]}, ${toDate.getFullYear()}`;
};

const dayDiffInclusive = (fromDate, toDate) =>
  Math.round((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000));

export const computePreviousPeriod = (fromStr, toStr) => {
  const fromDate = parseLocalYmd(fromStr);
  const toDate = parseLocalYmd(toStr);
  const lengthMs = toDate.getTime() - fromDate.getTime();
  const compareTo = new Date(fromDate);
  compareTo.setDate(compareTo.getDate() - 1);
  const compareFrom = new Date(compareTo);
  compareFrom.setTime(compareFrom.getTime() - lengthMs);
  return { from: formatDateInput(compareFrom), to: formatDateInput(compareTo) };
};

export const computePreviousWeek = (fromStr, toStr) => {
  const fromDate = parseLocalYmd(fromStr);
  const toDate = parseLocalYmd(toStr);
  fromDate.setDate(fromDate.getDate() - 7);
  toDate.setDate(toDate.getDate() - 7);
  return { from: formatDateInput(fromDate), to: formatDateInput(toDate) };
};

export const computePreviousMonth = (fromStr, toStr) => {
  const fromDate = parseLocalYmd(fromStr);
  const toDate = parseLocalYmd(toStr);
  const lengthDays = dayDiffInclusive(fromDate, toDate);

  let prevStart = new Date(fromDate.getFullYear(), fromDate.getMonth() - 1, fromDate.getDate());
  if (prevStart.getMonth() === fromDate.getMonth()) {
    prevStart = new Date(fromDate.getFullYear(), fromDate.getMonth(), 0);
  }

  const targetDow = fromDate.getDay();
  const forward = (targetDow - prevStart.getDay() + 7) % 7;
  const backward = (prevStart.getDay() - targetDow + 7) % 7;
  if (backward > 0 && backward < forward) {
    prevStart.setDate(prevStart.getDate() - backward);
  } else if (forward > 0) {
    prevStart.setDate(prevStart.getDate() + forward);
  }

  const prevEnd = new Date(prevStart);
  prevEnd.setDate(prevEnd.getDate() + lengthDays);
  return { from: formatDateInput(prevStart), to: formatDateInput(prevEnd) };
};

export const applyComparePreset = (preset, fromStr, toStr) => {
  if (!fromStr || !toStr) return null;
  if (preset === 'prev_week') return computePreviousWeek(fromStr, toStr);
  if (preset === 'prev_month') return computePreviousMonth(fromStr, toStr);
  return computePreviousPeriod(fromStr, toStr);
};

const COMPARE_PRESET_OPTIONS = [
  { key: 'previous', label: 'Предыдущий период' },
  { key: 'prev_week', label: 'Прошлая неделя' },
  { key: 'prev_month', label: 'Предыдущий месяц' },
];

const MAIN_PRESETS = [
  { label: 'Вчера', days: 1 },
  { label: 'Последние 7 дней', days: 7, highlight: true },
  { label: 'Последние 14 дней', days: 14 },
  { label: 'Последние 30 дней', days: 30 },
  { label: 'Последние 60 дней', days: 60 },
  { label: 'Последние 90 дней', days: 90 },
  { label: 'Последние 6 месяцев', months: 6 },
  { label: 'Последние 12 месяцев', months: 12 },
];

function DateRangeCalendar({
  month,
  onMonthChange,
  periodFrom,
  periodTo,
  compareFrom,
  compareTo,
  showCompare,
  onDateClick,
  selectingPeriod,
}) {
  const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  const weekDays = ['ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ', 'ВС'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const m = date.getMonth();
    const firstDay = new Date(year, m, 1);
    const lastDay = new Date(year, m + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, m, i));
    return days;
  };

  const isDateInRange = (date, from, to) => {
    if (!date || !from || !to) return false;
    const dateStr = formatDateInput(date);
    return dateStr >= from && dateStr <= to;
  };
  const isDateStart = (date, from) => date && from && formatDateInput(date) === from;
  const isDateEnd = (date, to) => date && to && formatDateInput(date) === to;

  const getDateClass = (date) => {
    if (!date) return '';
    const isToday = formatDateInput(date) === formatDateInput(new Date());
    const isMainStart = isDateStart(date, periodFrom);
    const isMainEnd = isDateEnd(date, periodTo);
    const isInMainRange = periodFrom && periodTo && isDateInRange(date, periodFrom, periodTo);
    const isCompareStart = showCompare && isDateStart(date, compareFrom);
    const isCompareEnd = showCompare && isDateEnd(date, compareTo);
    const isInCompareRange = showCompare && compareFrom && compareTo && isDateInRange(date, compareFrom, compareTo);

    let classes = 'w-10 h-10 flex items-center justify-center text-sm rounded transition-colors cursor-pointer ';
    if (isToday) classes += 'font-semibold ';
    if (isMainStart || isMainEnd) classes += 'bg-blue-600 text-white rounded-full z-10 relative ';
    else if (isCompareStart || isCompareEnd) classes += 'bg-slate-500 text-white rounded-full z-10 relative ';
    else if (isInMainRange) classes += 'bg-blue-100 text-blue-900 ';
    else if (isInCompareRange) classes += 'bg-gray-200 text-gray-700 ';
    else classes += 'hover:bg-gray-100 text-gray-700 ';
    return classes;
  };

  const currentMonthDays = getDaysInMonth(month);
  const nextMonth = new Date(month);
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  const nextMonthDays = getDaysInMonth(nextMonth);

  const selectionHint = !showCompare
    ? (periodFrom && !periodTo
      ? 'Основной период: выберите дату окончания'
      : 'Основной период: выберите дату начала')
    : selectingPeriod === 'main'
      ? (periodFrom && !periodTo
        ? 'Шаг 1 · Основной период: выберите дату окончания'
        : 'Шаг 1 · Основной период: выберите дату начала')
      : (compareFrom && !compareTo
        ? 'Шаг 2 · Период сравнения: выберите дату окончания'
        : 'Шаг 2 · Период сравнения: выберите дату начала');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
        <p className="text-sm text-gray-700">{selectionHint}</p>
        {showCompare && (
          <div className="flex items-center gap-2 text-xs shrink-0">
            <span className={`px-2 py-0.5 rounded-full ${selectingPeriod === 'main' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'}`}>
              Основной
            </span>
            <span className="text-gray-400">→</span>
            <span className={`px-2 py-0.5 rounded-full ${selectingPeriod === 'compare' ? 'bg-slate-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
              Сравнение
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => {
                const d = new Date(month);
                d.setMonth(d.getMonth() - 1);
                onMonthChange(d);
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <HiChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h4 className="text-sm font-semibold text-gray-900">
              {months[month.getMonth()]} {month.getFullYear()}
            </h4>
            <div className="w-6" />
          </div>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day) => (
              <div key={day} className="text-xs text-gray-500 font-medium text-center py-1">{day}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {currentMonthDays.map((date, idx) => (
              <div key={idx} onClick={() => date && onDateClick(date)} className={getDateClass(date)}>
                {date ? date.getDate() : ''}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="w-6" />
            <h4 className="text-sm font-semibold text-gray-900">
              {months[nextMonth.getMonth()]} {nextMonth.getFullYear()}
            </h4>
            <button
              type="button"
              onClick={() => {
                const d = new Date(month);
                d.setMonth(d.getMonth() + 1);
                onMonthChange(d);
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <HiChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day) => (
              <div key={day} className="text-xs text-gray-500 font-medium text-center py-1">{day}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {nextMonthDays.map((date, idx) => (
              <div key={idx} onClick={() => date && onDateClick(date)} className={getDateClass(date)}>
                {date ? date.getDate() : ''}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-6 text-xs text-gray-600 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-600 rounded-full" />
          <span>Начало/конец основного</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-100" />
          <span>Основной период</span>
        </div>
        {showCompare && (
          <>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-slate-500 rounded-full" />
              <span>Начало/конец сравнения</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-200" />
              <span>Период сравнения</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Модалка выбора основного периода и периода сравнения
 * (как на вкладке Реклама).
 */
export default function PeriodDateRangeModal({
  open,
  onClose,
  periodFrom,
  periodTo,
  compareFrom,
  compareTo,
  compareDirty = false,
  comparePreset = 'previous',
  showCompare = true,
  onShowCompareChange,
  onApply,
}) {
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectingPeriod, setSelectingPeriod] = useState('main');
  const [tempPeriodFrom, setTempPeriodFrom] = useState(periodFrom || '');
  const [tempPeriodTo, setTempPeriodTo] = useState(periodTo || '');
  const [tempCompareFrom, setTempCompareFrom] = useState(compareFrom || '');
  const [tempCompareTo, setTempCompareTo] = useState(compareTo || '');
  const [tempCompareDirty, setTempCompareDirty] = useState(compareDirty);
  const [tempComparePreset, setTempComparePreset] = useState(comparePreset || 'previous');

  useEffect(() => {
    if (!open) return;
    setTempPeriodFrom(periodFrom || '');
    setTempPeriodTo(periodTo || '');
    setTempCompareFrom(compareFrom || '');
    setTempCompareTo(compareTo || '');
    setTempCompareDirty(compareDirty);
    setTempComparePreset(comparePreset || 'previous');
    setSelectingPeriod('main');
  }, [open, periodFrom, periodTo, compareFrom, compareTo, compareDirty, comparePreset]);

  useEffect(() => {
    if (!open || tempCompareDirty || tempComparePreset === 'manual') return;
    if (!tempPeriodFrom || !tempPeriodTo) return;
    const range = applyComparePreset(tempComparePreset, tempPeriodFrom, tempPeriodTo);
    if (!range) return;
    setTempCompareFrom(range.from);
    setTempCompareTo(range.to);
  }, [tempPeriodFrom, tempPeriodTo, tempCompareDirty, tempComparePreset, open]);

  const resolveComparePreset = (preset = tempComparePreset) =>
    (preset === 'manual' ? 'previous' : preset);

  const applyTempMainPeriod = (fromStr, toStr) => {
    const preset = resolveComparePreset();
    setTempPeriodFrom(fromStr);
    setTempPeriodTo(toStr);
    setTempComparePreset(preset);
    setTempCompareDirty(false);
    setSelectingPeriod('main');
    const range = applyComparePreset(preset, fromStr, toStr);
    if (range) {
      setTempCompareFrom(range.from);
      setTempCompareTo(range.to);
    }
  };

  const applyTempComparePreset = (preset) => {
    if (!tempPeriodFrom || !tempPeriodTo) return;
    const range = applyComparePreset(preset, tempPeriodFrom, tempPeriodTo);
    if (!range) return;
    setTempComparePreset(preset);
    setTempCompareFrom(range.from);
    setTempCompareTo(range.to);
    setTempCompareDirty(false);
    setSelectingPeriod('main');
  };

  const handleCalendarDateClick = (date) => {
    const dateStr = formatDateInput(date);
    const mainComplete = Boolean(tempPeriodFrom && tempPeriodTo);
    const compareComplete = Boolean(tempCompareFrom && tempCompareTo);

    const pickingCompare =
      showCompare
      && selectingPeriod === 'compare'
      && mainComplete
      && !compareComplete;

    if (!pickingCompare) {
      if (!tempPeriodFrom || mainComplete) {
        setTempPeriodFrom(dateStr);
        setTempPeriodTo('');
        setSelectingPeriod('main');
        return;
      }

      let fromStr = tempPeriodFrom;
      let toStr = dateStr;
      if (dateStr < tempPeriodFrom) {
        fromStr = dateStr;
        toStr = tempPeriodFrom;
        setTempPeriodFrom(fromStr);
      }
      setTempPeriodTo(toStr);

      const preset = resolveComparePreset();
      setTempComparePreset(preset);
      setTempCompareDirty(false);
      if (showCompare) {
        const range = applyComparePreset(preset, fromStr, toStr);
        if (range) {
          setTempCompareFrom(range.from);
          setTempCompareTo(range.to);
        }
        setSelectingPeriod('compare');
      }
      return;
    }

    if (!tempCompareFrom || compareComplete) {
      setTempCompareFrom(dateStr);
      setTempCompareTo('');
      setTempCompareDirty(true);
      setTempComparePreset('manual');
      return;
    }
    if (dateStr >= tempCompareFrom) {
      setTempCompareTo(dateStr);
    } else {
      setTempCompareTo(tempCompareFrom);
      setTempCompareFrom(dateStr);
    }
    setTempCompareDirty(true);
    setTempComparePreset('manual');
    setSelectingPeriod('main');
  };

  const handleApply = () => {
    onApply?.({
      periodFrom: tempPeriodFrom,
      periodTo: tempPeriodTo,
      compareFrom: tempCompareFrom,
      compareTo: tempCompareTo,
      compareDirty: tempCompareDirty,
      comparePreset: tempComparePreset,
      showCompare,
    });
    onClose?.();
  };

  if (!open) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <div
        className="modal-panel rounded-2xl max-w-5xl w-full mx-4 my-8 flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-header flex justify-between items-center p-6 rounded-t-2xl">
          <h3 className="text-xl font-semibold text-gray-800">Выбор периода</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <HiX className="w-6 h-6" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="w-64 border-r border-white/30 p-6 overflow-y-auto">
            <div className="space-y-1 mb-6">
              {MAIN_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => {
                    const to = new Date();
                    const from = new Date(to);
                    if (preset.months) from.setMonth(from.getMonth() - preset.months);
                    else from.setDate(from.getDate() - preset.days);
                    applyTempMainPeriod(formatDateInput(from), formatDateInput(to));
                  }}
                  className={`w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors ${
                    preset.highlight ? 'flex items-center gap-2' : ''
                  }`}
                >
                  {preset.highlight && <HiCheck className="w-4 h-4 text-purple-600" />}
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-700">Сравнение</span>
                {typeof onShowCompareChange === 'function' && (
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showCompare}
                      onChange={(e) => onShowCompareChange(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                  </label>
                )}
              </div>
              {showCompare && (
                <div className="space-y-1">
                  {COMPARE_PRESET_OPTIONS.map((opt) => {
                    const active = tempComparePreset === opt.key && !tempCompareDirty;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => applyTempComparePreset(opt.key)}
                        className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors flex items-center gap-2 ${
                          active ? 'bg-indigo-50 text-indigo-800' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <span className="w-4 shrink-0">
                          {active ? <HiCheck className="w-4 h-4 text-indigo-600" /> : null}
                        </span>
                        <span className="font-medium">{opt.label}</span>
                      </button>
                    );
                  })}
                  {tempComparePreset === 'manual' && (
                    <div className="px-3 py-2 text-xs text-gray-500 flex items-center gap-2">
                      <HiCheck className="w-4 h-4 text-slate-500" />
                      Задан вручную на календаре
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 p-6 overflow-y-auto">
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Основной: от</label>
                  <input
                    type="date"
                    value={tempPeriodFrom}
                    onChange={(e) => {
                      const fromStr = e.target.value;
                      setTempPeriodFrom(fromStr);
                      setSelectingPeriod('main');
                      if (fromStr && tempPeriodTo && fromStr <= tempPeriodTo) {
                        applyTempMainPeriod(fromStr, tempPeriodTo);
                      } else {
                        setTempCompareDirty(false);
                        if (tempComparePreset === 'manual') setTempComparePreset('previous');
                      }
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Основной: до</label>
                  <input
                    type="date"
                    value={tempPeriodTo}
                    onChange={(e) => {
                      const toStr = e.target.value;
                      setTempPeriodTo(toStr);
                      if (tempPeriodFrom && toStr && tempPeriodFrom <= toStr) {
                        applyTempMainPeriod(tempPeriodFrom, toStr);
                      } else {
                        setSelectingPeriod('main');
                        setTempCompareDirty(false);
                        if (tempComparePreset === 'manual') setTempComparePreset('previous');
                      }
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {showCompare && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Сравнить от</label>
                    <input
                      type="date"
                      value={tempCompareFrom}
                      onChange={(e) => {
                        setTempCompareFrom(e.target.value);
                        setTempCompareDirty(true);
                        setTempComparePreset('manual');
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Сравнить до</label>
                    <input
                      type="date"
                      value={tempCompareTo}
                      onChange={(e) => {
                        setTempCompareTo(e.target.value);
                        setTempCompareDirty(true);
                        setTempComparePreset('manual');
                        setSelectingPeriod('main');
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              <DateRangeCalendar
                month={calendarMonth}
                onMonthChange={setCalendarMonth}
                periodFrom={tempPeriodFrom}
                periodTo={tempPeriodTo}
                compareFrom={tempCompareFrom}
                compareTo={tempCompareTo}
                showCompare={showCompare}
                onDateClick={handleCalendarDateClick}
                selectingPeriod={selectingPeriod}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer flex justify-end gap-3 p-6 rounded-b-2xl">
          <button type="button" onClick={onClose} className="glass-btn-secondary px-6 py-2 font-medium">
            ОТМЕНА
          </button>
          <button type="button" onClick={handleApply} className="glass-btn px-6 py-2 font-medium">
            ПРИМЕНИТЬ
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
